import type {ReactNode} from 'react'
import isNil from 'lodash/isNil'
import {
  isStaging,
  isTestEnv,
  isProductionEnv
} from '@freckle/educator-entities/ts/common/helpers/common-api-helper'
import type {DistrictAttrs} from '@freckle/educator-entities/ts/roster/models/district'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import {logout} from '@freckle/school/ts/common/components/dashboard/logout'
import Notification from '@freckle/school/ts/common/helpers/notification'
import type {SchoolAdminAttrs} from '@freckle/school/ts/users/models/school-admin'

type WindowWithPendo = Window & {
  pendo: {
    initialize: (...args: unknown[]) => void
  }
}

export function handleFetchingError(xhr: XMLHttpRequest): ReactNode {
  if (xhr.status === 401 || xhr.status === 403) {
    logout()
  } else {
    Notification.displayErrorNotification('Could not connect with the server. Please try again')
    console.error(`Error when fetching: ${xhr.statusText}`)
  }
  return null
}

type PendoVisitorIdT = {
  id: string
  type: 'global' | 'rgp' | 'local'
}

const getPrimaryVisitorIdData = (schoolAdmin: SchoolAdminAttrs): PendoVisitorIdT => {
  if (!isNil(schoolAdmin.renaissanceRPIdentifier)) {
    return {
      id: schoolAdmin.renaissanceRPIdentifier,
      type: 'rgp'
    }
  } else if (schoolAdmin.gurId !== null && schoolAdmin.gurId !== '') {
    return {
      id: schoolAdmin.gurId,
      type: 'global'
    }
  } else {
    return {
      id: `${schoolAdmin.id}school-admin`, //appending school-admin to differentiate between teacher and student ids
      type: 'local'
    }
  }
}

export function initalizePendo(
  schoolAdmin: SchoolAdminAttrs,
  districts: Array<DistrictAttrs>,
  schools: Array<SchoolAttrs>
) {
  if (Boolean(isStaging()) || Boolean(isTestEnv())) {
    return
  }
  const district = districts[0] ?? null
  const tagEnvironment = (value: string | number): string => {
    // eslint-disable-next-line no-extra-boolean-cast
    const env = Boolean(isTestEnv()) ? 'test-' : Boolean(isProductionEnv()) ? '' : 'dev-'
    return `${env}${value}`
  }
  ;(window as unknown as WindowWithPendo).pendo.initialize({
    visitor: {
      // standard metadata
      id: tagEnvironment(getPrimaryVisitorIdData(schoolAdmin).id),
      role_fr: 'school-admin',

      // basic data for Renaissance Pendo
      visitor_id_type: tagEnvironment(getPrimaryVisitorIdData(schoolAdmin).type),
      global_user_id: schoolAdmin.gurId,
      rgp_user_id: schoolAdmin.renaissanceRPIdentifier,
      local_user_id: schoolAdmin.id,
      user_type: 'administrator', //user_type should be values for OneRoster role, outlined here: https://www.imsglobal.org/oneroster-v11-final-specification#_Toc480452025

      // custom metadata
      created_at_fr: schoolAdmin.createdAt.toISOString(),
      split_io_tests_fr: [],
      split_io_treatments_fr: [],
      app_fr: 'school'
    },
    account: {
      // standard metadata
      id: !isNil(district) ? tagEnvironment(`${district.name}-district-${district.id}`) : null,
      name: !isNil(district) ? district.name : null,
      is_paying_fr: true,

      // custom metadata
      school_ids_fr: schools.map(school => school.id),
      district_id_fr: !isNil(district) ? district.id : null,
      administrative_area_fr: !isNil(district) ? district.administrativeArea : null,
      has_class_link_id_fr: !isNil(district?.classLinkDistrictId),
      has_clever_id_fr: !isNil(district?.cleverDistrictId)
    }
  })
}
